import { useCallback, useEffect, useState } from 'react'
import { useAuth as useWorkOSAuth } from '@workos-inc/authkit-react'
import {
  useHttpInterceptorEffect,
  executeTokenRefresh,
} from './useHttpInterceptorEffect'
import useConfig from '../useConfig'

type AuthTokenSetupProps = {
  initApiToken: (
    token: string,
    refreshToken: () => Promise<string | undefined>
  ) => Promise<void>
  refreshApiToken: (token: string, logout: () => void) => Promise<void>
  setNoAccess?: () => void
}

export const useAuth = ({
  initApiToken,
  refreshApiToken,
  setNoAccess,
}: AuthTokenSetupProps) => {
  const { getAccessToken, signIn, signOut } = useWorkOSAuth()
  const [isTokenProvided, setIsTokenProvided] = useState(false)
  const { AUTH_REDIRECT_URI } = useConfig()

  const fetchAccessToken = useCallback(async () => {
    try {
      const accessToken = await getAccessToken()
      return accessToken
    } catch (e) {
      console.error('Failed to get access token:', e)
      const searchParams = new URLSearchParams(window.location.search)
      const context = searchParams.get('context') ?? undefined
      await signIn({ context })
    }
  }, [getAccessToken, signIn])

  const logoutCb = useCallback(() => {
    // TODO uncomment when ready to support
    //Gleap.clearIdentity()
    signOut({ returnTo: AUTH_REDIRECT_URI })
  }, [signOut, AUTH_REDIRECT_URI])

  // Используем единый механизм refresh через executeTokenRefresh
  const refreshAuthToken = useCallback(async () => {
    return executeTokenRefresh(async () => {
      const token = await fetchAccessToken()
      if (!token) {
        throw new Error('No access token')
      }
      // Attempt to refresh API token and reconnect; if it fails, ensure logout
      try {
        await refreshApiToken(token, logoutCb)
      } catch (err) {
        logoutCb()
        throw err
      }
      return token
    })
  }, [fetchAccessToken, refreshApiToken, logoutCb])

  const setAuthToken = useCallback(async () => {
    const token = await fetchAccessToken()
    if (!token) {
      return
    }
    await initApiToken(token, refreshAuthToken)
    setIsTokenProvided(true)
    return token
  }, [fetchAccessToken, initApiToken, refreshAuthToken])

  useHttpInterceptorEffect({
    refreshToken: refreshAuthToken,
    onError: ({ status }) => {
      if (
        status === 'token_invalid' ||
        status === 'network_error' ||
        !setNoAccess
      ) {
        logoutCb()
      } else {
        setNoAccess()
      }
    },
  })

  useEffect(() => {
    setAuthToken()
  }, [setAuthToken])

  return {
    isTokenProvided,
    refreshToken: refreshAuthToken,
    logout: logoutCb,
  }
}
